import { useMemo } from 'react';
import RichContent from '../content/RichContent';

// Deterministic shuffle based on question id (stable across renders)
function shuffleWithSeed(arr, seed, correctId) {
    const doShuffle = (s) => {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
            s = (s * 1103515245 + 12345) & 0x7fffffff;
            const j = s % (i + 1);
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return { shuffled, nextSeed: s };
    };

    let s = typeof seed === 'number' ? seed : String(seed).split('').reduce((a, c) => a + c.charCodeAt(0), 0);

    for (let attempt = 0; attempt < 5; attempt++) {
        const { shuffled, nextSeed } = doShuffle(s);
        const correctIdx = shuffled.findIndex(r => r.id === correctId);
        const originalIdx = arr.findIndex(r => r.id === correctId);
        if (correctIdx !== originalIdx || arr.length <= 2) return shuffled;
        s = nextSeed + attempt + 1;
    }

    return doShuffle(s).shuffled;
}

export default function QuestionCard({
    question,
    selectedAnswer,
    onAnswer,
    showResult,
    correctAnswer,
    apprentissageFeedback = false,
    // Mode audit uniquement : propose « Je ne sais pas » (sentinelle '?').
    // Signal de lacune assumée — plus fiable qu'une réponse au hasard.
    allowDontKnow = false,
}) {
    const activeReponses = useMemo(() => {
        const active = (question.reponses || []).filter(r => r.isActive !== false);
        return shuffleWithSeed(active, question.id, correctAnswer || question.bonne_reponse);
    }, [question.id, question.reponses]);

    // In apprentissage feedback mode, only show the selected answer + correct answer
    const visibleReponses = useMemo(() => {
        if (!apprentissageFeedback || !showResult || !selectedAnswer) return activeReponses;
        return activeReponses.filter(r =>
            r.id === selectedAnswer || r.id === correctAnswer
        );
    }, [activeReponses, apprentissageFeedback, showResult, selectedAnswer, correctAnswer]);

    // Compute display labels from the full list (stable A/B/C/D even when filtered)
    const labelMap = useMemo(() => {
        const map = {};
        activeReponses.forEach((r, idx) => {
            map[r.id] = String.fromCharCode(65 + idx);
        });
        return map;
    }, [activeReponses]);

    const getClass = (reponseId) => {
        let cls = 'answer-btn';
        if (selectedAnswer === reponseId) cls += ' selected';
        if (showResult) {
            if (reponseId === correctAnswer) cls += ' correct';
            else if (selectedAnswer === reponseId) cls += ' incorrect';
        }
        return cls;
    };

    return (
        <div className="question-card">
            <div className="question-enonce">
                <RichContent blocks={question.enonce} />
            </div>

            <div className="answers-list">
                {visibleReponses.map((reponse) => (
                    <button
                        key={`${question.id}-${reponse.id}`}
                        className={getClass(reponse.id)}
                        onClick={() => !showResult && onAnswer(reponse.id)}
                        disabled={showResult}
                    >
                        <span className="answer-label">{labelMap[reponse.id]}.</span>
                        <div className="answer-content">
                            <RichContent blocks={reponse.content} />
                        </div>
                        {showResult && reponse.id === correctAnswer && (
                            <svg className="answer-check" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                                <path d="M5 12.5 L10 17 L19 7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                ))}
            </div>

            {allowDontKnow && !showResult && (
                <button
                    type="button"
                    className={`answer-dontknow ${selectedAnswer === '?' ? 'selected' : ''}`}
                    onClick={() => selectedAnswer !== '?' && onAnswer('?')}
                    title="Aucune pénalité cachée : la notion est simplement marquée « à apprendre » — plus utile qu'une réponse au hasard."
                >
                    🤷 Je ne sais pas{selectedAnswer === '?' ? ' — c\'est noté' : ''}
                </button>
            )}
            {selectedAnswer === '?' && showResult && (
                <div className="answer-dontknow-note">
                    Tu as répondu « Je ne sais pas » — la bonne réponse est indiquée ci-dessus, et la notion est marquée à apprendre.
                </div>
            )}
        </div>
    );
}
